import { hash01, lerp, smoothstep, valueNoise } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData, type Instance } from './chunk';
import { SCAR_CELL } from './overlay';

/**
 * Settlement ground wear: human presence must show on the ground.
 *
 * A main-thread transform in the applyEnv family: freshly built chunk data
 * comes in, the mortal-pressure ledger is read per vertex, and the ground
 * under a camp packs toward warm bare earth. Chained *between* env and the
 * burn overlay — wear sits over the living colours and under the scars,
 * because a burned camp reads as burned, not trampled.
 *
 * The baseline underneath is never touched, and this module owns no state:
 * pressure lives with the mortal layer (sim/mortals/impact.ts) and decays on
 * the world clock there. Rebuild the chunk with an empty ledger and the land
 * is exactly what the seed says — the camp fades because the ledger forgets.
 */

/** The slice of the mortal-pressure ledger this transform reads. */
export interface PressureReader {
  at(wx: number, wz: number, now: number): {
    trampling: number; visitation: number; harvest: number; woodcutting: number;
  } | null;
}

/** Trodden earth — the colour of ground that feet have beaten bare. */
const PACKED = [0.30, 0.25, 0.18] as const;
/** Worked-over stands go dusty: the canopy thins toward dry olive-brown. */
const DUSTY = [0.30, 0.26, 0.17] as const;

/**
 * ## What the ledger actually holds
 *
 * `tools/wear.ts` runs a god-free world for 240 days, finds where the people
 * settled, and reads the ground out from the hearth they built. Two things came
 * back, and only one of them is stable.
 *
 * The unstable one is the magnitude. How hard a band wears its ground depends
 * entirely on how much of the year it spends standing on it, and that is a
 * property of `sim/mortals/decisions.ts`, not of this file. Measured across a
 * rewrite of the band layer the reading under a hearth moved from
 * `trampling 0.035` to `trampling 0.979` — a factor of twenty-eight. Any
 * constant here fitted to one of those is wrong in the other.
 *
 * The stable one is the *ceiling*, because the channels saturate at 1. So this
 * calibrates against saturation: the numbers below are fractions of what the
 * ground can hold, not of what one build of the simulation happened to
 * deposit. A band that lives somewhere approaches the ceiling and clears its
 * camp; a band that only passes through never gets near it and leaves the
 * forest standing. That is the design law — the land remembers repetition —
 * expressed as arithmetic instead of as a fitted constant.
 *
 * What was genuinely broken, and stayed broken through both regimes, is the
 * felling rule. It read `woodcutting + trampling / 2` against 0.62, and
 * woodcutting is **exactly zero everywhere, in every world measured** — so its
 * own maximum was 0.5 and it could never fire at all. A camp stood in unbroken
 * forest because the arithmetic forbade anything else. Nothing here may depend
 * on woodcutting alone.
 */
/** Ground the people merely passed over. Must not show. */
const USE_TRACE = 0.06;
/** By here the ground is beaten bare: about two fifths of what a cell can hold. */
const USE_PACKED = 0.60;

/**
 * The one reading everything here is driven by: how *used* this ground is.
 * Trampling packs hardest, coming-and-going about half as much, and cut wood
 * counts for more than either — when there is any.
 */
const channels = (p: { trampling: number; visitation: number; woodcutting: number }) =>
  p.trampling + p.visitation * 0.5 + p.woodcutting * 1.2;

/**
 * Metres of the ragged margin a camp wears into the ground around it, and how
 * far the wear may swing across it. The lattice is 32 m; this is deliberately
 * finer, so it breaks the cell edge without inventing a second grid of its own.
 */
const EDGE_SCALE = 11, EDGE_SWING = 0.35;
const S_EDGE = 0x64c7;

/**
 * How used the ground at (wx, wz) is — read across the lattice, not out of one
 * cell of it.
 *
 * The ledger is a sparse store on 32 m cells and `at()` is a nearest-cell
 * lookup, so a camp forty metres across covers about nine whole cells and
 * nothing in between. Sampled point-blank and pushed through a curve steep
 * enough to show, that is not a clearing: it is a **diamond** of packed earth
 * with 32 m sides, which is exactly what the first photograph of this showed.
 * The old code never met it only because its response was too faint to see the
 * shape it was drawing.
 *
 * So: bilinear across the four cells around the point, which turns the blocks
 * into a smooth mound, and then a swing of value noise finer than the lattice,
 * which turns the mound's edge ragged. Both are pure functions of world
 * position — no camera, no clock, no visit order — so a camp's outline is the
 * same outline at every LOD and on every machine.
 */
/**
 * How packed the ground at a point is by living on it, 0..1 — the same
 * reading `applyWear` pushes the ground colour through, exported so the
 * render-side groundcover can be *swept* where people live. A camp whose
 * floor is packed to bare earth must not sprout ankle grass between the
 * shelters; the litter's dead debris is a different question and stays.
 */
export function wornAt(
  pressure: PressureReader, wx: number, wz: number, now: number,
): number {
  return smoothstep(USE_TRACE, USE_PACKED, useAt(pressure, wx, wz, now));
}

function useAt(pressure: PressureReader, wx: number, wz: number, now: number): number {
  const fx = wx / SCAR_CELL - 0.5, fz = wz / SCAR_CELL - 0.5;
  const i = Math.floor(fx), j = Math.floor(fz);
  const u = fx - i, v = fz - j;
  const cell = (gx: number, gz: number): number => {
    const p = pressure.at((gx + 0.5) * SCAR_CELL, (gz + 0.5) * SCAR_CELL, now);
    return p ? channels(p) : 0;
  };
  const a = cell(i, j), b = cell(i + 1, j), c = cell(i, j + 1), d = cell(i + 1, j + 1);
  const use = lerp(lerp(a, b, u), lerp(c, d, u), v);
  if (use <= 0) return 0;
  return use * lerp(1 - EDGE_SWING, 1 + EDGE_SWING,
                    valueNoise(wx / EDGE_SCALE, wz / EDGE_SCALE, S_EDGE));
}

/** scaleY below this is understory — grasses and shrubs that feet destroy. */
const SMALL = 3.0;
/**
 * Above this height a tree is worth more standing than felled — shade, mast,
 * and the labour of taking it down. It was 14 m, which in a coast forest spares
 * the entire canopy: `tools/wear.ts` counts five trees over 14 m inside one
 * camp and only eight beneath it, so the rule that was meant to open a clearing
 * could reach barely half the stand. At 18 m it takes the mid-storey and leaves
 * the emergents, which is both what people do and what makes a clearing read as
 * one — a ring of giants around open ground, not a bald patch.
 *
 * **36 since 2026-08-21**, because the trees roughly doubled (`world/chunk.ts`,
 * the height law). The number was never about 18 metres as such; it was about
 * standing where the mid-storey ends and the emergents begin, and that line
 * moved with them. Left at 18 it would have spared nearly every canopy tree on
 * the island and put every camp in pristine forest — the exact failure the
 * paragraph above describes, arrived at from the other side.
 */
export const SPARED = 36;
/**
 * A camp that reads as three huts in pristine forest is a camp nobody
 * believes: the first thing people do is clear the ground they live on and
 * burn what they cleared. Trees inside a well-worked camp come down; the big
 * emergents at the edge survive, which is what makes a clearing read as a
 * clearing rather than as a bald patch.
 *
 * Both of these are *feathered*, not switched: each plant is kept or taken by a
 * hash of its own position against the local strength, so the clearing has a
 * ragged edge that follows the pressure gradient. A hard threshold on a field
 * this smooth draws a disc, and nothing in a landscape is a disc.
 *
 * Both windows sit low enough that a saturated camp core clears completely even
 * at the bottom of the edge noise's swing — otherwise the noise stops being a
 * ragged margin and starts punching holes through the middle of the clearing.
 * They are still far above anything a band that merely passes through deposits.
 */
const STRIP_LO = 0.10, STRIP_HI = 0.30;
const FELL_LO = 0.18, FELL_HI = 0.42;
/** Distinct salts, so which plants are trampled never correlates with which are felled. */
const S_STRIP = 0x77a1, S_FELL = 0x88b3;

/**
 * Stamp settlement wear onto freshly built chunk data, main thread. Mutates
 * in place and returns the same object, like applyEnv. `size` is an optional
 * hint — the pressure ledger's cell count — so an untouched world skips the
 * whole pass instead of probing every vertex of every chunk.
 */
export function applyWear(
  data: ChunkData,
  pressure: PressureReader,
  now: number,
  size?: number,
  /** Places a camp may not clear — see `world/things.ts`. */
  spare?: (x: number, z: number) => boolean,
): ChunkData {
  if (size === 0) return data;
  const step = CHUNK_SIZE / (data.res - 1);
  const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;

  // ground: packed toward bare earth, on a curve that reaches most of the way
  // at a hearth's own reading and nothing at all under a week-old footpath
  for (let j = 0; j < data.res; j++) for (let i = 0; i < data.res; i++) {
    const w = smoothstep(USE_TRACE, USE_PACKED,
                         useAt(pressure, ox + i * step, oz + j * step, now)) * 0.85;
    if (w <= 0.004) continue;
    const k = (j * data.res + i) * 3;
    data.colour[k] = lerp(data.colour[k], PACKED[0], w);
    data.colour[k + 1] = lerp(data.colour[k + 1], PACKED[1], w);
    data.colour[k + 2] = lerp(data.colour[k + 2], PACKED[2], w);
  }

  // vegetation: a trampled camp centre goes bare — the understory is walked to
  // death and the smaller trees are cut for fuel and frames — and what is left
  // standing dulls toward dusty. Structure never changes here: what survives
  // keeps its spot and its size, so nothing moves or resizes as a camp grows.
  const kept: Instance[] = [];
  for (const inst of data.vegetation) {
    const use = useAt(pressure, inst.x, inst.z, now);
    if (use <= USE_TRACE) { kept.push(inst); continue; }
    // A thing history has made important is not cleared to make room, and
    // people build around it. This is the sacred tree in the middle of the
    // village, and it is the one place promotion cannot reach from its own
    // end of the chain: it runs last and only recolours, so a plant felled
    // here was gone before there was anything left to mark.
    //
    // Note what this does *not* disturb: which of its neighbours come down.
    // Both hashes are of the plant's own position, not of a running counter,
    // so sparing one tree leaves every other plant's fate exactly as it was.
    if (!spare?.(inst.x, inst.z)) {
      // the plant's own position decides, so the same plant is taken (or spared)
      // at every LOD and in every rebuild — a clearing does not shimmer
      const hx = Math.round(inst.x * 8), hz = Math.round(inst.z * 8);
      if (inst.scaleY < SMALL) {
        if (hash01(hx, hz, S_STRIP) < smoothstep(STRIP_LO, STRIP_HI, use)) continue;
      } else if (inst.scaleY < SPARED &&
                 hash01(hx, hz, S_FELL) < smoothstep(FELL_LO, FELL_HI, use)) {
        continue;                                 // felled for fuel and frames
      }
    }
    const w = smoothstep(USE_TRACE, USE_PACKED, use) * 0.55;
    if (w > 0.004) {
      inst.colour = [
        lerp(inst.colour[0], DUSTY[0], w),
        lerp(inst.colour[1], DUSTY[1], w),
        lerp(inst.colour[2], DUSTY[2], w),
      ];
    }
    kept.push(inst);
  }
  data.vegetation = kept;
  return data;
}
